import { watch } from 'vue'
import { useRuntimeConfig } from '#app'

/* Constants */
import {
  StoryGroups,
  GROUP_RUSSIA,
  USER_STATUS_TAGS,
  USER_PLACEHOLDERS,
} from '@/constants/components/app-stories'

/* Types */
import {
  StoryManagerConfig,
  StoryManager,
} from '@/services/inAppStory/inAppStories.types'

/* Stores */
import { useUserStore } from '@/store/user/user'
import { useCityStore } from '@/store/city/city'

/* Services */
import { getInAppStoriesId } from '@/services/inAppStory/inAppStories.utils'

const SDK_URL = 'https://sdk.inappstory.com/v2.10.2/dist/js/IAS.js'

export const storyManagerConfig: StoryManagerConfig = {
  apiKey: '',
  userId: '', // usually - hash from real user identifier
  tags: [], // Array<string>
}

type PromiseReturn = {
  storyManager: StoryManager
  appearanceManager: any
}

const getUserId = () => {
  const userStore = useUserStore()

  // Если авторизован - используем clientId
  if (userStore.info && userStore.info.clientId) {
    return userStore.info.clientId
  }

  // Если не автоизован - используем session
  return getInAppStoriesId() || 'default'
}

const getUserName = () => {
  const userStore = useUserStore()

  // Если авторизован - используем fullName
  if (userStore.info && userStore.info.clientId) {
    return userStore.info.fullName
  }

  return ''
}

let STORY_MANAGER: StoryManager | null = null
let APPEARANCE_MANAGER: any = null

export const injectStoriesScript = () => {
  if (!window) {
    console.warn('Ошибка загрузки InAppStories скрипта. Window не найден')
    return
  }

  // Уже загружен - сбрасываем и загружаем по новой
  if (window.IASReady) {
    document.querySelector('#ias-wjs')?.remove()
    APPEARANCE_MANAGER = null
    STORY_MANAGER = null
    delete window.IASReady
  }

  window.IASReady = (function (d, s, id) {
    const fjs = d.getElementsByTagName(s)[0]
    const st = window.IASReady || {}

    if (d.getElementById(id)) return st
    const js = d.createElement(s) as HTMLScriptElement
    js.id = id
    js.src = SDK_URL
    js.async = true
    fjs.parentNode?.insertBefore(js, fjs)
    st._e = []
    st.ready = function (f: any) {
      st._e.push(f)
    }
    return st
  })(document, 'script', 'ias-wjs')

  return new Promise<PromiseReturn>((resolve) => {
    window.IASReady.ready(async () => {
      // Добавляем API_KEY
      const { IN_APP_STORIES_API_KEY } = useRuntimeConfig().public
      storyManagerConfig.apiKey = IN_APP_STORIES_API_KEY as string

      // Добавляем UserId
      const userId = getUserId()
      storyManagerConfig.userId = userId

      // Добавляем плейсхолдеры
      const placeholders = getStoriesPlaceholders()
      if (placeholders) {
        storyManagerConfig.placeholders = placeholders
      }

      // Добавляем теги для определения сторисов для города
      const tags = await getStoriesTags()
      storyManagerConfig.tags = tags

      // StoryManager singleton instance
      STORY_MANAGER = new window.IAS.StoryManager(
        storyManagerConfig,
      ) as StoryManager

      // AppearanceManager instance
      APPEARANCE_MANAGER = new window.IAS.AppearanceManager()

      // Добавляем watch на логин пользователя
      setWatcherClientId(STORY_MANAGER)

      resolve({
        storyManager: STORY_MANAGER,
        appearanceManager: APPEARANCE_MANAGER,
      })
    })
  })
}

// Изменяет userId для сторисов, если пользователь залогинился/разлогинился
const setWatcherClientId = (storyManager: StoryManager) => {
  const userStore = useUserStore()

  watch(
    () => userStore.info,
    () => {
      const userId = getUserId()
      storyManager.setUserId(userId)
    },
  )
}

// Добавляет теги для определения сторисов для города
const getStoriesTags = async () => {
  const cityStore = useCityStore()
  const cityDomainPrefix = cityStore.cityInfo.domain_prefix
  const countryName = cityStore.cityInfo.country?.name

  // Добавляем текущий город в теги
  const tags = [cityDomainPrefix]

  // Получаем список тэгов-категорий
  const storyGroups = await fetchStoryGroups()

  // Проверяем, входит ли город в категори.
  for (const groupTag of Object.keys(storyGroups)) {
    const cityList = storyGroups[groupTag as keyof typeof storyGroups]

    // Если город входит в категорию в список тэгов
    if (cityList.includes(cityDomainPrefix)) {
      tags.push(groupTag)
    }
  }

  // Доабвляем категорию для городов России
  if (countryName === GROUP_RUSSIA.value) {
    tags.push(GROUP_RUSSIA.key)
  }

  const userStore = useUserStore()
  const isAuthenticated = userStore.isAuthenticated

  if (!isAuthenticated) return tags

  // Теги только для авторизованного пользователя
  tags.push(USER_STATUS_TAGS.isAuth)

  const countOrders = userStore.statistics?.ordersCount

  if (countOrders === 0) {
    tags.push(USER_STATUS_TAGS.empty)
    return tags
  }

  if (countOrders === 1) {
    tags.push(USER_STATUS_TAGS.oneOrder)
    return tags
  }

  if (countOrders && countOrders > 1) {
    tags.push(USER_STATUS_TAGS.old)
    return tags
  }

  return tags
}

const fetchStoryGroups = () => {
  // TODO тут будет запрос API для получения групп
  return StoryGroups
}

const getStoriesPlaceholders = () => {
  const userStore = useUserStore()
  const isAuthenticated = userStore.isAuthenticated

  if (!isAuthenticated) return null

  const placeholders: Dict<string> = {}

  const userName = getUserName()
  if (userName) {
    placeholders[USER_PLACEHOLDERS.username] = userName
  }

  const bonusCount = String(userStore.statistics?.bonus || 0)
  const ordersCount = String(userStore.statistics?.ordersCount || 0)
  const usedBonusesCount = String(userStore.statistics?.debited || 0)

  placeholders[USER_PLACEHOLDERS.bonusCount] = bonusCount
  placeholders[USER_PLACEHOLDERS.ordersCount] = ordersCount
  placeholders[USER_PLACEHOLDERS.usedBonusesCount] = usedBonusesCount

  return Object.keys(placeholders).length ? placeholders : null
}

export const getDesktopStoriesOptions = () => {
  return {
    navigation: {
      showControls: true,
      controlsColor: '#2c2f39',
    },
    card: {
      title: {
        display: true,
        position: 'cardInsideBottom',
        textAlign: 'left',
        lineClamp: 3,
        color: '#fff',
        font: 'normal 800 14px/1.2 "Raleways", "Raleways fallback", verdana, sans-serif',
      },
      gap: 8,
      height: 178,
      variant: 'quad',
      border: {
        gradient: '#FF5044',
        width: 2,
        gap: 2,
        radius: 30,
      },
      opened: {
        border: {
          gradient: '#9EA7B6',
          width: 2,
          gap: 2,
          radius: 30,
        },
      },
    },
    sidePadding: 25,
    bottomPadding: 0,
    topPadding: 0,
    extraCss: `
          .stories-list-controls .control-left,
          .stories-list-controls .control-right {
            background-color: #fff;
            border-radius: 50%;
            box-shadow: 0 4px 5px rgba(65, 58, 91, 0.15);
            width: 0 !important;
            height: 0 !important;
            opacity: 0;
            transition: all 0.1s ease;
          }

          .stories-list-controls .control-left {
            left: 0 !important;
          }

          .stories-list-controls .control-right {
            right: 0 !important;
          }

          .stories-list-controls .control-left.active,
          .stories-list-controls .control-right.active {
            width: 40px !important;
            height: 40px !important;
            opacity: 1;
          }
        `, // Выносим кнопки для перелистывания в бок
  }
}

export const getMobileStoriesOptions = (isMobile: boolean) => {
  return {
    navigation: {
      showControls: false,
    },
    card: {
      title: {
        display: true,
        position: 'cardInsideBottom',
        textAlign: 'left',
        lineClamp: 3,
        color: '#fff',
        font: '600 12px/normal "Raleways", "Raleways fallback", verdana, sans-serif',
      },
      gap: 8,
      height: 98,
      variant: 'quad',
      border: {
        gradient: '#FF5044',
        width: 2,
        gap: 2,
        radius: 15,
      },
      opened: {
        border: {
          gradient: '#9EA7B6',
          width: 2,
          gap: 2,
          radius: 15,
        },
      },
    },
    sidePadding: 0,
    bottomPadding: 0,
    topPadding: 0,
    extraCss: `
      .stories-list__viewport-inner {
        padding-right: ${isMobile ? 20 : 48}px;
      }

      .stories-slide__content {
        padding: 5px !important;
      }
    `, // Выносим кнопки для перелистывания в бок
  }
}
